import React from 'react'
import { Controller, Control, FieldErrors } from 'react-hook-form'
import BaseQuestion from '../base-question'
import s from '../styles.module.css'

interface MultiSelectQuestionProps {
  question: {
    id: number
    text: string
    isRequired?: boolean
    type: string
    maxSelections?: number
    responses?: Array<{
      id: number
      text: string
      subText?: string
    }>
  }
  // RHF mode
  control?: Control<any>
  errors?: FieldErrors<any>
  // Zustand mode
  value?: number[]
  error?: string
  onChange?: (value: number[]) => void
}

const MultiSelectQuestion: React.FC<MultiSelectQuestionProps> = ({
  question,
  control,
  errors,
  value,
  error,
  onChange,
}) => {
  const isRHFMode = !!control && !!errors
  const maxSelections =
    question.maxSelections || question.responses?.length || 0

  const handleToggle = (
    responseId: number,
    currentValue: number[] | undefined
  ) => {
    const currentResponses = Array.isArray(currentValue) ? currentValue : []
    const normalizedResponseId =
      typeof responseId === 'string' ? parseInt(responseId, 10) : responseId

    if (
      currentResponses.some(
        id =>
          (typeof id === 'string' ? parseInt(id, 10) : id) ===
          normalizedResponseId
      )
    ) {
      // Remove
      onChange?.(
        currentResponses.filter(
          id =>
            (typeof id === 'string' ? parseInt(id, 10) : id) !==
            normalizedResponseId
        )
      )
    } else {
      // Add
      if (currentResponses.length < maxSelections) {
        onChange?.([...currentResponses, normalizedResponseId])
      }
    }
  }

  if (isRHFMode) {
    // RHF mode - use Controller
    return (
      <BaseQuestion question={question} control={control} errors={errors}>
        <div className={s.selectAllThatApplyText}>Select all that apply</div>
        <div className={s.responses}>
          <Controller
            name={`question_${question.id}`}
            control={control}
            rules={{
              required: question.isRequired ? 'This field is required' : false,
            }}
            render={({ field }) => (
              <>
                {question.responses?.map(response => {
                  const responseId =
                    typeof response.id === 'string'
                      ? parseInt(response.id, 10)
                      : response.id
                  const isSelected =
                    Array.isArray(field.value) &&
                    field.value.some(
                      val =>
                        (typeof val === 'string' ? parseInt(val, 10) : val) ===
                        responseId
                    )
                  const canSelect =
                    !isSelected &&
                    Array.isArray(field.value) &&
                    field.value.length >= maxSelections

                  return (
                    <label
                      key={response.id}
                      className={`${s.responseOption} ${
                        isSelected ? s.selected : ''
                      } ${canSelect ? s.disabled : ''}`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={e => {
                          const currentResponses = Array.isArray(field.value)
                            ? field.value
                            : []
                          if (e.target.checked) {
                            field.onChange([...currentResponses, responseId])
                          } else {
                            field.onChange(
                              currentResponses.filter(
                                id =>
                                  (typeof id === 'string'
                                    ? parseInt(id, 10)
                                    : id) !== responseId
                              )
                            )
                          }
                        }}
                        disabled={canSelect}
                        className={s.responseInput}
                      />
                      <div className={s.responseTextWrapper}>
                        <span className={s.responseText}>{response.text}</span>
                        {response.subText && (
                          <span className={s.responseSubText}>
                            {response.subText}
                          </span>
                        )}
                      </div>
                    </label>
                  )
                })}
              </>
            )}
          />
        </div>
      </BaseQuestion>
    )
  } else {
    // Zustand mode - use direct props
    const currentValue = Array.isArray(value) ? value : []
    return (
      <BaseQuestion question={question} error={error}>
        <div className={s.selectAllThatApplyText}>Select all that apply</div>
        <div className={s.responses}>
          {question.responses?.map(response => {
            const responseId =
              typeof response.id === 'string'
                ? parseInt(response.id, 10)
                : response.id
            const isSelected = currentValue.some(
              id =>
                (typeof id === 'string' ? parseInt(id, 10) : id) === responseId
            )
            const canSelect =
              !isSelected && currentValue.length >= maxSelections

            return (
              <label
                key={response.id}
                className={`${s.responseOption} ${
                  isSelected ? s.selected : ''
                } ${canSelect ? s.disabled : ''}`}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggle(responseId, currentValue)}
                  disabled={canSelect}
                  className={s.responseInput}
                />
                <div className={s.responseTextWrapper}>
                  <span className={s.responseText}>{response.text}</span>
                  {response.subText && (
                    <span className={s.responseSubText}>
                      {response.subText}
                    </span>
                  )}
                </div>
              </label>
            )
          })}
        </div>
      </BaseQuestion>
    )
  }
}

export default MultiSelectQuestion
